import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useShare } from '../context/ShareContext';
import { useCommunity } from '../context/CommunityContext';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import ReportDialog from '../components/ReportDialog';
import Seo from '../components/Seo';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { trackProfileView } from '../lib/analytics';

const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { providers = [], dbCommercialOffers = [], commercialOffers = [], user } = useShare();
  const { subscriptionIds, toggleProviderSubscription, featureAvailable } = useCommunity();
  const [notFoundReady, setNotFoundReady] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const allOffers = dbCommercialOffers.length > 0 ? dbCommercialOffers : commercialOffers;
  const directOffer = allOffers.find((offer) => String(offer.id) === String(id));
  const providerId = directOffer?.providerId || directOffer?.provider_id || id;

  const provider = useMemo(() => {
    const storedProvider = providers.find((entry) => String(entry.id) === String(providerId));
    if (storedProvider) return storedProvider;
    if (!directOffer) return null;

    return {
      id: providerId,
      name: directOffer.providerName || directOffer.provider_name || directOffer.title || 'Anbieter',
      address: directOffer.address,
      description: directOffer.provider_description || directOffer.description,
      logo_url: directOffer.logo_url,
      website: directOffer.website,
    };
  }, [providers, providerId, directOffer]);

  const offers = useMemo(() => {
    const matchingOffers = allOffers.filter((offer) => {
      const offerProviderId = offer.providerId || offer.provider_id;
      return String(offerProviderId) === String(providerId) && offer.is_active !== false;
    });

    if (matchingOffers.length > 0) return matchingOffers;
    return directOffer ? [directOffer] : [];
  }, [allOffers, providerId, directOffer]);

  useEffect(() => {
    if (provider) {
      setNotFoundReady(false);
      trackProfileView({ providerId: provider.id, providerName: provider.name, type: 'commercial' });
      return undefined;
    }

    const timer = window.setTimeout(() => setNotFoundReady(true), 1800);
    return () => window.clearTimeout(timer);
  }, [provider?.id]);

  const toggleSubscription = async () => {
    if (!user) return navigate('/auth');
    setSubscribing(true);
    try {
      await toggleProviderSubscription(providerId);
    } catch (error) {
      alert(error.message || 'Das Anbieter-Abo konnte nicht geändert werden.');
    } finally {
      setSubscribing(false);
    }
  };

  if (!provider && !notFoundReady) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
        <p className="text-gray-500">Anbieter wird geladen...</p>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="text-4xl text-red-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Anbieter nicht gefunden</h1>
        <button type="button" onClick={() => navigate('/feed')} className="mt-4 text-blue-600 font-bold hover:underline">Zurück zur Übersicht</button>
      </div>
    );
  }

  const subscribed = subscriptionIds.has(String(providerId));
  const pageUrl = `https://schleishare.de/provider/${providerId}`;
  const description = String(provider.description || `${provider.name} bei SchleiShare: aktuelle Mittagstische und Speisekarten aus der Schlei-Region.`).slice(0, 180);
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    name: provider.name,
    description,
    image: provider.logo_url || undefined,
    address: provider.address || undefined,
    url: provider.website || pageUrl,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Seo title={provider.name} description={description} image={provider.logo_url} url={pageUrl} type="restaurant" structuredData={structuredData} />
      <button type="button" onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 text-gray-500 hover:text-blue-600 font-medium">
        <SafeIcon icon={FiIcons.FiArrowLeft} /> Zurück
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-8 p-8 flex flex-col md:flex-row gap-8 items-center md:items-start"
      >
        <div className="w-32 h-32 md:w-48 md:h-48 shrink-0 bg-gray-50 rounded-2xl border border-gray-100 p-4 flex items-center justify-center">
          {provider.logo_url ? (
            <img src={provider.logo_url} alt={provider.name} className="w-full h-full object-contain" />
          ) : (
            <SafeIcon icon={FiIcons.FiBriefcase} className="text-6xl text-gray-300" />
          )}
        </div>
        <div className="flex-grow text-center md:text-left">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{provider.name}</h1>
          {provider.address && (
            <p className="text-gray-500 font-medium flex items-center justify-center md:justify-start gap-2 mb-4">
              <SafeIcon icon={FiIcons.FiMapPin} className="text-blue-600" />
              {provider.address}
            </p>
          )}
          {provider.description && <p className="text-gray-700 leading-relaxed max-w-3xl mb-6">{provider.description}</p>}
          <div className="flex flex-wrap justify-center gap-2 md:justify-start">
            <button
              type="button"
              onClick={toggleSubscription}
              disabled={subscribing || !featureAvailable}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold ${subscribed ? 'bg-blue-100 text-blue-800' : 'bg-blue-600 text-white'} disabled:opacity-50`}
            >
              <SafeIcon icon={subscribed ? FiIcons.FiBellOff : FiIcons.FiBell} />
              {subscribed ? 'Abo beenden' : 'Anbieter abonnieren'}
            </button>
            <FavoriteButton targetType="provider" targetId={providerId} />
            <ShareButton title={provider.name} text={description} url={pageUrl} />
          </div>
          <div className="mt-5"><ReportDialog targetType="provider" targetId={providerId} /></div>
        </div>
      </motion.div>

      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <SafeIcon icon={FiIcons.FiList} className="text-blue-600" />
        Aktuelle Angebote von {provider.name}
      </h2>

      {offers.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <SafeIcon icon={FiIcons.FiInbox} className="text-6xl text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Aktuell keine Angebote</h3>
          <p className="text-gray-500">Dieser Anbieter hat im Moment keinen aktiven Mittagstisch veröffentlicht.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {offers.map((item) => {
            const menu = parseArray(item.weekly_menu);
            return (
              <Link
                to={`/listing/${item.id}`}
                key={item.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group h-full hover:shadow-md transition-all"
              >
                <div className="relative h-40 overflow-hidden bg-gray-100 shrink-0">
                  {(item.imageUrl || item.image_url) ? (
                    <img src={item.imageUrl || item.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={item.title} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <SafeIcon icon={FiIcons.FiImage} className="text-4xl" />
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 bg-blue-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-md">Mittagstisch</div>
                  <FavoriteButton targetType="commercial_offer" targetId={item.id} label={false} className="absolute right-2 top-2 px-2 py-1.5" />
                </div>
                <div className="p-4 flex-grow flex flex-col">
                  <h3 className="text-base font-bold text-gray-900 leading-tight mb-2 line-clamp-2">{item.title}</h3>
                  {menu.length > 0 && (
                    <span className="text-xs font-bold text-blue-700 mb-2 bg-blue-50 px-2 py-1 rounded-md w-fit">
                      {menu.length} {menu.length === 1 ? 'Gericht' : 'Gerichte'}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProviderProfile;
